import { supertypeClass, property, remote, amorphicStatic } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';

import _ from 'lodash';
import { Customer } from '../customer/Customer';
import { Policy } from '../customer/Policy';
import { WebServiceUtils } from '../webservice/WebServiceUtils';
import { Utils } from '../Utils';
import { FormFieldText } from './FormFieldText';
import { FormFieldCheckbox } from './FormFieldCheckbox';
import { FormFieldRadioGroup } from './FormFieldRadioGroup';
import { FormFieldSelect } from './FormFieldSelect';
import { Person } from '../customer/Person';
import { Address } from '../customer/Address';
import { Phone } from '../customer/Phone';
import { Beneficiary } from '../customer/Beneficiary';
import { PackageForm } from './PackageForm';
import { FormField } from './FormField';
import { Form } from './Form';
import { Package } from './Package';
import { PolicyDoc } from '../customer/PolicyDoc';
import { MetadataHierarchy } from '../metadata/MetadataHierarchy';
import { PdfController } from '../../js/pdfUtil/PdfController';
import { MetadataNode } from '../MetadataNode';
import { RetrieveSignedDocumentsClient } from '../../../common/js/wsclient/retrieveSignedDocuments/RetrieveSignedDocumentsClient';
import * as fs from 'fs';
import request from 'request-promise-native';
import puppeteer from 'puppeteer';
import * as constants from 'constants';
import { SnapshotServiceUtils } from '../SnapshotServiceUtils';
import { Product } from '../static/Product';
import { Assumptions } from '../static/Assumptions';
import { CommonUtils } from '../static/CommonUtils';
import { CoverpathUtils } from '../../../coverpath-common/js/static/CoverpathUtils';
import { SecurityUtils } from '../../../coverpath-common/js/static/SecurityUtils';
import { CommonUtilsBackend } from '../static/CommonUtilsBackend';
import { CoverpathPolicy } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { POLICY_DOC_SOURCES, POLICY_DOC_TYPES } from '../constants/policy-doc';
import jwt from 'jsonwebtoken';
import { ServerSideRendererConfig } from '../../../hit-snapshot-service/js/ServicesApi';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';

const INSURED_RECIPIENT_ID = '2';
const OWNER_RECIPIENT_ID = '3';
const SECOND_OWNER_RECIPIENT_ID = '4';
const APPLICATION_PDF_DOCUMENT_ID = '8675309';
const ILLUSTRATION_PDF_DOCUMENT_ID = '8675310';
const MEC_PDF_DOCUMENT_ID = '8675311';
// this is because it's guaranteed to be the third document,
// the first being the app, second being the app signature
const SIGNATURE_PAGE_KEY = '2';
const ILLUSTRATION_FETCH_KEY = '3';
const MEC_FETCH_KEY = '4';

interface HttpOptions {
  method?: string;
  uri: string;
  agentOptions: {
    rejectUnauthorized: boolean;
    strictSSL: boolean;
    secureOptions: number;
  };
  timeout: number;
  headers;
  encoding?: string;
  json?;
}

export interface DocuSignEnvelope {
  request: {
    emailSubject: string;
    status: string;
    allowRecursiveRecipients: string;
    allowReassign: string;
    compositeTemplates: {}[];
    brandId: string;
    eventNotification: {
      url: string;
      requireAcknowledgment: string;
      loggingEnabled: string;
      includeHMAC: string;
      envelopeEvents: {
        envelopeEventStatusCode: string;
        includeDocuments: string;
      }[];
      eventData: {
        version: string;
        format: string;
      };
      includeTimeZone: string;
      includeEnvelopeVoidReason: string;
    };
  };
  log: string[];
  envelopeMap: {
    docMap: {};
    recipientMap: {};
  };
}

@supertypeClass({ toClient: ['admin'], toServer: true })
export class DocuSign extends Everything {
  static includeApplicationAsPDF = function (policy): boolean {
    // Non ICC policies use a screenshot
    const policyConfigs = MetadataHierarchy.getConfigurationsForPolicy(policy);

    return (
      policy.wasApplicationStateNonICC() ||
      (policyConfigs && !policyConfigs.isICC) ||
      Product.products[policy.product].channel === Product.channels.CAS ||
      Product.products[policy.product].channel === Product.channels.MMDirect ||
      Product.products[policy.product].channel === Product.channels.LifeBridge
    );
  };

  @property({ sensitiveData: true })
  password: string;

  @property()
  integratorKey: string = 'COVE-0bcef4ca-74e3-4e1b-8411-5533481dc048';

  @property()
  accountKey: string = 'b53331df-7714-4830-9f05-99cd579df333';

  @property()
  recipient: string = 'sams@elsamman.com';

  @property()
  subject: string;

  @property({ isLocal: true })
  properties;

  @property({ isLocal: true, type: Array, getType: () => String })
  entities: Array<string>;

  @property({ isLocal: true, type: Array, getType: () => String })
  arrayEntities: Array<string>;

  @property({ isLocal: true, type: Array, getType: () => Boolean })
  isArrayEntity: Array<boolean>;

  @property({ isLocal: true })
  undef: string = 'undefined';

  @property({ isLocal: true })
  func: string = 'function';

  @property({ toClient: false, toServer: true, getType: () => PdfController })
  pdfController: PdfController;

  @property({ getType: () => Customer })
  customer: Customer;

  @property()
  envelopeId: string;

  @property()
  adminMode: boolean;

  @property()
  controller;

  @property()
  metadata: MetadataNode;

  @property()
  forms;

  @property()
  express: boolean;

  @property()
  doSend: boolean;

  @property()
  error: boolean;

  constructor() {
    super();
  }

  static new(controller?) {
    return new DocuSign().init(controller);
  }

  init(controller?) {
    this.controller = controller;
    return this;
  }

  serverInit() {}

  getPrefix() {
    return '';
  }

  async httpOptions(reqUrl: string, runModes?, type?: string): Promise<HttpOptions> {
    const cfg = amorphicStatic.config.configStore.webservice,
      webServiceName = 'RetrieveSignedDocuments',
      svcCfg = WebServiceUtils.clientCfg(webServiceName),
      wsclient = RetrieveSignedDocumentsClient.getClient(svcCfg),
      headers = {};
    let endpointUrl;

    if (this.adminMode || type === 'resend') {
      endpointUrl = svcCfg.liveEndpointUrl + svcCfg.accountKey + '/' + reqUrl;
    } else {
      endpointUrl = wsclient.updateEndpointUrl(null, runModes).replace(/envelopes$/, reqUrl);
    }

    await wsclient._addHeaders(headers);
    if (type && this.amorphic.config.nconf.get('environment') !== 'production') {
      headers['x-hit-form-type'] = type;
    }

    return {
      uri: endpointUrl,
      agentOptions: {
        rejectUnauthorized: svcCfg.authEnabled,
        strictSSL: svcCfg.authEnabled,
        secureOptions: constants.SSL_OP_NO_TLSv1_1 | constants.SSL_OP_NO_TLSv1,
      },
      timeout: cfg.get('httpTimeoutSecs'),
      headers: headers,
    };
  }

  createProperties() {
    this.properties = {};
    this.entities = ['None'];
    this.arrayEntities = ['None'];
    this.isArrayEntity = [];

    const history = {};

    try {
      this.setProperties('', Customer, history, false);
      this.setProperties('application.primaryPolicy', Policy, history, false);
    } catch (error) {
      LoggerSingleton.getInstance().error({
        module: 'DocuSign',
        function: 'createProperties',
        category: Category.request,

        error: {
          name: error?.name,
          message: error?.message,
          stack: error?.stack,
          isHumanRelated: false,
        },
      });
    }

    this.entities.sort();
    this.arrayEntities.sort();

    _.each(
      this.entities,
      function (e) {
        const p = this.properties[e];

        if (p) {
          p.sort();
        }
      }.bind(this)
    );
  }

  storeForms(forms) {
    this.forms = forms;
  }

  generateCompositePdf(pdfInBase64: string, documentId: string, sequence: string, formName: string) {
    return {
      compositeTemplateId: new Date().getTime(),
      inlineTemplates: [
        {
          sequence: sequence,
          recipients: {
            editors: [],
            signers: [], // will be filled in later
          },
          documents: [
            {
              name: formName,
              fileExtension: 'PDF',
              documentId: documentId,
              documentBase64: pdfInBase64,
            },
          ],
        },
      ],
    };
  }

  setTabsForPdfComposite(composite, insuredTabs, ownerTabs, secondOwnerTabs, illustrationSignPage: number = 6) {
    if (composite.inlineTemplates) {
      for (let i = 0; i < composite.inlineTemplates.length; i++) {
        const template = composite.inlineTemplates[i];
        if (template.documents) {
          for (let j = 0; j < template.documents.length; j++) {
            const document = template.documents[j];
            this.setTabsForDocument(document, insuredTabs, ownerTabs, secondOwnerTabs, illustrationSignPage);
          }
        }
      }
    }
  }

  setTabsForDocument(document, insuredTabs, ownerTabs, secondOwnerTabs, illustrationSignPage) {
    if (/BasicIllustration/.test(document.name)) {
      const tabs = ownerTabs ? ownerTabs : insuredTabs;
      const recipientId = ownerTabs ? OWNER_RECIPIENT_ID : INSURED_RECIPIENT_ID;
      tabs.signHereTabs.push({
        xPosition: '20',
        yPosition: '495',
        documentId: document.documentId,
        pageNumber: illustrationSignPage,
        recipientId: recipientId,
      });
      tabs.dateSignedTabs.push({
        xPosition: '300',
        yPosition: '533',
        documentId: document.documentId,
        pageNumber: illustrationSignPage,
        recipientId: recipientId,
      });
    }
    if (document.name === 'a5030_mec') {
      if (ownerTabs || insuredTabs) {
        const tabs = ownerTabs ? ownerTabs : insuredTabs;
        const recipientId = ownerTabs ? OWNER_RECIPIENT_ID : INSURED_RECIPIENT_ID;
        tabs.signHereTabs.push({
          xPosition: '244',
          yPosition: '420',
          documentId: document.documentId,
          pageNumber: '1',
          recipientId: recipientId,
        });
        tabs.dateSignedTabs.push({
          xPosition: '488',
          yPosition: '464',
          documentId: document.documentId,
          pageNumber: '1',
          recipientId: recipientId,
        });
      }
      if (secondOwnerTabs) {
        const tabs = secondOwnerTabs;
        const recipientId = SECOND_OWNER_RECIPIENT_ID;
        tabs.signHereTabs.push({
          xPosition: '244',
          yPosition: '455',
          documentId: document.documentId,
          pageNumber: '1',
          recipientId: recipientId,
        });
        tabs.dateSignedTabs.push({
          xPosition: '488',
          yPosition: '500',
          documentId: document.documentId,
          pageNumber: '1',
          recipientId: recipientId,
        });
      }
    }
  }

  async generateEnvelope(policy: Policy, formsAndComposites, isAdditionalPackage?: boolean): Promise<DocuSignEnvelope> {
    const forms = formsAndComposites.forms,
      composites = [],
      express = this.express,
      doSend = this.doSend,
      envMap = {
        docMap: {},
        recipientMap: {},
      },
      log = [];

    if (!forms || forms.length === 0) {
      throw new Error('Cannot find forms to send');
    }

    //list of form templates & their tabs
    const templates = [];
    let lastId = '';
    let dupCnt = 0;
    let numberOfPrefixedForms = DocuSign.includeApplicationAsPDF(policy) ? 1 : 0;

    numberOfPrefixedForms = isAdditionalPackage ? 0 : numberOfPrefixedForms;
    for (let i = 0, len = forms.length; i < len; ++i) {
      dupCnt = lastId === forms[i].form.templateId ? dupCnt + 1 : 0;
      lastId = forms[i].form.templateId;

      const sequence = String(i + 1 + numberOfPrefixedForms);
      const type = forms[i].form.getType();
      const formLanguage = forms[i].form.getLanguage() || 'en';

      const isSignature = /Signature/.test(forms[i].form.name);

      const mapFormDef = {
        sequence: isSignature ? SIGNATURE_PAGE_KEY : sequence,
        language: formLanguage,
      };

      if (type !== undefined) {
        if (envMap.docMap[type] === undefined) {
          envMap.docMap[type] = [mapFormDef];
        } else {
          envMap.docMap[type].push(mapFormDef);
        }
      }

      const template = {
        templateId: forms[i].form.templateId,
        seq: sequence,
        tabs: {
          listTabs: [],
          radioGroupTabs: [],
          checkBoxTabs: [],
          textTabs: [],
        },
      };

      const t = template.tabs;

      getFormTabs.call(
        this,
        t.listTabs,
        t.radioGroupTabs,
        t.checkBoxTabs,
        t.textTabs,
        forms[i].mappingForm || forms[i].form,
        dupCnt,
        log
      );

      templates.push(template);
    }

    //list of recipients
    let rec;

    const signerList = [];
    const channelName = policy.getChannelInfo().name;
    const language = policy.submittedLanguage ? policy.submittedLanguage : 'en';
    const isInstant = policy.isAccelerated();
    const reqToCheck = _.find(this.customer.application.primaryPolicy.currentStageRequirements, function (req) {
      return (
        req === 'Send Insurability Statement for signature' || req === 'Resend Insurability Statement for signature'
      );
    });
    const emailContent = this.buildEmailContent(channelName, language, express);

    const name = policy.insured.person.getFullName();
    const accessCode = await SecurityUtils.getPiiValueByKey(policy.insured.person.SSN, 4);

    rec = {
      name: name,
      // If insured is juvenile and has no signer (owner == signer), insured's email == owner's email, so this docusign is
      // actually sent to owner
      // If insured is juvenile and has signer (owner != signer), insured's email = signer's email, so this docusign is
      // actually sent to signer
      email: policy.insured.person.email,
      recipientId: INSURED_RECIPIENT_ID,
      routingOrder: '2',
      roleName: 'Insured',
      templateLocked: true,
      accessCode: accessCode,
      emailNotification: {
        supportedLanguage: language,
        emailBody: emailContent.body,
        emailSubject: emailContent.subject,
      },
    };

    signerList.push(rec);
    envMap.recipientMap['Insured'] = rec;

    if (formsAndComposites.composites && formsAndComposites.composites.length > 0) {
      for (
        let index = 0, generatedCompositesLength = formsAndComposites.composites.length;
        index < generatedCompositesLength;
        index++
      ) {
        const composite = formsAndComposites.composites[index];

        if (this.compositeContainsDocOfId(composite, ILLUSTRATION_PDF_DOCUMENT_ID)) {
          const mapFormDef = {
            sequence: ILLUSTRATION_FETCH_KEY,
            language: 'en',
          };

          const type = 'BasicIllustration';
          if (envMap.docMap[type] === undefined) {
            envMap.docMap[type] = [mapFormDef];
          } else {
            envMap.docMap[type].push(mapFormDef);
          }
          LoggerSingleton.getInstance().info(
            {
              module: 'DocuSign',
              function: 'generateEnvelope',
              category: Category.request,
            },
            'Adding BasicIllustration to docMap for policy._id=' + policy._id
          );
        }

        if (this.compositeContainsDocOfId(composite, MEC_PDF_DOCUMENT_ID)) {
          const mapFormDef = {
            sequence: MEC_FETCH_KEY,
            language: 'en',
          };

          const type = 'a5030_mec';
          if (envMap.docMap[type] === undefined) {
            envMap.docMap[type] = [mapFormDef];
          } else {
            envMap.docMap[type].push(mapFormDef);
          }
          LoggerSingleton.getInstance().info(
            {
              module: 'DocuSign',
              function: 'generateEnvelope',
              category: Category.request,
            },
            'Adding MEC form to docMap for policy._id=' + policy._id
          );
        }

        const insuredTabs = {
          listTabs: [],
          radioGroupTabs: [],
          checkBoxTabs: [],
          textTabs: [],
          signHereTabs: [],
          dateSignedTabs: [],
        };

        this.setTabsForPdfComposite(composite, insuredTabs, null, null);
        this.assignTabsToComposite(composite, signerList, insuredTabs, null, null);
        composites.push(composite);
      }
    }

    //make req to DocuSign to send envelope to recipients
    for (let iTemplate = 0, numTemplates = templates.length; iTemplate < numTemplates; ++iTemplate) {
      const envelopeTemplate = templates[iTemplate];

      let isSignature = false;
      // application is always first composite, and signature is always first template
      // signature must immediately follow application
      if (iTemplate === 0 && DocuSign.includeApplicationAsPDF(policy)) {
        isSignature = true;
      }

      const composite = {
        compositeTemplateId: new Date().getTime() + envelopeTemplate.seq,
        serverTemplates: [
          {
            sequence: envelopeTemplate.seq,
            templateId: envelopeTemplate.templateId,
          },
        ],
        inlineTemplates: [
          {
            sequence: isSignature ? SIGNATURE_PAGE_KEY : envelopeTemplate.seq,
            recipients: {
              editors: [],
              signers: [], // will be filled in later
            },
          },
        ],
      };
      // this type of composite only has insured tabs
      this.assignTabsToComposite(composite, signerList, envelopeTemplate.tabs, null, null);

      if (isSignature) {
        composites.splice(1, null, composite);
      } else {
        composites.push(composite);
      }
    }

    // Note: In local or CI environment, need to expose api-gateway via ngrok, and point havenDocuSignCallbackUrl to this in order for Docusign callback to work. (Docusign callback does not accept ports in url)

    const retval = {
      request: {
        emailSubject: 'Sign Your Life Insurance Application',
        status: doSend ? 'sent' : 'created',
        allowRecursiveRecipients: 'true',
        allowReassign: 'false',
        compositeTemplates: composites,
        brandId: undefined,
        eventNotification: {
          url: this.amorphic.config.nconf.get('havenDocuSignCallbackUrl'),
          requireAcknowledgment: 'true',
          loggingEnabled: 'true',
          includeHMAC: 'true',
          envelopeEvents: [
            {
              envelopeEventStatusCode: 'Completed',
              includeDocuments: 'false',
            },
          ],
          eventData: {
            version: 'legacy',
            format: 'xml',
          },
          includeTimeZone: 'true',
          includeEnvelopeVoidReason: 'true',
        },
      },
      log: log,
      envelopeMap: envMap,
    };

    let brandIdConfig = 'docusignBrandId';

    if (language === 'es') {
      brandIdConfig = brandIdConfig + 'Es';
    }

    retval.request.brandId = this.amorphic.config.configStore[policy.sourceAppName].get(brandIdConfig);

    return Promise.resolve(retval);

    //make form fields into Docusign tabs
    function getFormTabs(listTabs, radioGroupTabs, checkBoxTabs, textTabs, form, param, log) {
      function resolveParam(ref) {
        return ref.replace(/Varying\(\)/, 'Varying(' + param + ')');
      }
      let value;
      log.push('---Mapping Form: ' + form.name + ' has ' + form.fields.length + ' fields');

      // Walk through all fields
      for (let formIndex = 0; formIndex < form.fields.length; ++formIndex) {
        const field = form.fields[formIndex];
        let label;

        // Make sure entity and property exist
        if (field.getEntity() && field.getProperty()) {
          // Determine whether array and how many times it repeats
          let entity = field.entity.replace(/^customer\./);
          let refTokens = entity.split('.');
          let ref = '';
          let repeat = 1;

          for (let tokenIndex = 0; tokenIndex < refTokens.length; ++tokenIndex) {
            ref += (ref.length > 0 ? '.' : '') + refTokens[tokenIndex];

            if (this.isArrayEntity[ref]) {
              refTokens[tokenIndex] = refTokens[tokenIndex] + this.getIteratorPrefix();
              repeat = this.getValue('this.customer.' + resolveParam(ref) + '.length');
              break;
            }
          }

          // Go through each iteration (non-arrays repeat once)
          for (let fieldIterator = 0; fieldIterator < repeat; ++fieldIterator) {
            // Update array reference
            label = field.labelKey || field.groupName;

            entity = field.entity.replace(/^customer\./);
            refTokens = entity.split('.');
            ref = '';

            for (let secondTokenIndex = 0; secondTokenIndex < refTokens.length; ++secondTokenIndex) {
              ref += (ref.length > 0 ? '.' : '') + refTokens[secondTokenIndex];

              if (this.isArrayEntity[ref]) {
                refTokens[secondTokenIndex] = refTokens[secondTokenIndex] + this.getIteratorPrefix();
                label = label.replace(/\*/, fieldIterator + 1);
              }
            }

            ref = 'this.customer.' + resolveParam(refTokens.join('.'));
            this.customer.fieldIterator = fieldIterator;

            // Entity must exist
            if (!this.getValue(ref)) {
              log.push(label + '=' + value + ' Entity Missing');
              continue;
            }

            // Look for entity level depends on
            if (!ref.match(/[\(\[]/)) {
              const entityDependsOn = this.getValue('typeof(' + ref + 'DependsOn)');

              if (entityDependsOn === 'function' && !this.getValue(ref + 'DependsOn()')) {
                log.push(label + '=' + value + ' Entity DependsOn');
                continue;
              }
            }

            const properties = this.getValue(ref + '.amorphicClass.getProperties()["' + field.property + '"]');

            ref = resolveParam(ref + '.' + field.property);

            // Look for property level depends on
            if (!ref.match(/\(/)) {
              const propDependsOn = this.getValue('typeof(' + ref + 'DependsOn)');

              if (propDependsOn === 'function' && !this.getValue(ref + 'DependsOn()')) {
                log.push(label + '=' + value + ' Prop DependsOn');
                continue;
              }
            }

            // Grab the value
            value = this.getValue(ref);

            if (properties && properties.format) {
              this.controller.value = value;
              value = this.controller[properties.format].call();
            } else if (value instanceof Date) {
              value = Utils.dateToMdy(value);
            }

            log.push(label + '=' + value);

            // put into a tab field for docuSign based on type. '\\*' sets all same-labelled fields at once
            if (field instanceof FormFieldText) {
              textTabs.push({
                tabLabel: '\\*' + label,
                value: value || '',
                locked: true,
              });
            }

            if (field instanceof FormFieldCheckbox) {
              if (field.booleanValue && value !== null) {
                checkBoxTabs.push({
                  tabLabel: '\\*' + label,
                  selected: value.toString() === field.booleanValue,
                  locked: true,
                });
              } else {
                checkBoxTabs.push({
                  tabLabel: '\\*' + label,
                  selected: value === 'true',
                  locked: true,
                });
              }
            }

            if (field instanceof FormFieldRadioGroup) {
              const values = {};
              let gotValues = false;

              for (let index = 0; index < field.buttons.length; index++) {
                if (field.buttons[index].key && field.buttons[index].value) {
                  if (field.buttons[index].key.match(/;/)) {
                    const listToIterate = field.buttons[index].key.split(';');

                    for (let x = 0; x < listToIterate.length; x++) {
                      values[listToIterate[x]] = field.buttons[index].value;
                    }
                  } else {
                    values[field.buttons[index].key] = field.buttons[index].value;
                  }

                  gotValues = true;
                }
              }

              if (gotValues) {
                value = values[String(value)];
              } else if (value === true) {
                value = 'Yes';
              } else if (value === false || value === null) {
                value = 'No';
              }

              if (value) {
                radioGroupTabs.push({
                  groupName: label,
                  radios: [
                    {
                      value: value.toString(),
                      selected: true,
                      locked: true,
                    },
                  ],
                });
              } else {
                log[log.length - 1] = log[log.length - 1] + '  !! Not Mapped !!';
              }
            }

            if (field instanceof FormFieldSelect) {
              listTabs.push({
                tabLabel: '\\*' + label,
                listItems: [
                  {
                    value: value,
                    selected: true,
                  },
                ],
                locked: true,
              });
            }
          }
        } else {
          log.push(label + ' Missing entity or prop on field');
        }
      }
    }
  }

  compositeContainsDocOfId(composite, documentId) {
    if (composite.inlineTemplates.length) {
      if (composite.inlineTemplates[0].documents.length) {
        const documents = composite.inlineTemplates[0].documents;
        return _.find(documents, (doc) => {
          return doc.documentId === documentId;
        });
      }
    }
    return false;
  }

  //utility function
  shallowMerge(...args: any[]) {
    //args: variable list of objects to sequentially merge into a new one
    const newObj = {};

    for (let arg = 0; arg < args.length; ++arg) {
      const oldObj = args[arg];

      for (const prop in oldObj) {
        if (oldObj.hasOwnProperty(prop)) {
          newObj[prop] = oldObj[prop];
        }
      }
    }

    return newObj;
  }

  assignTabsToComposite(composite, signers, insuredTabs, ownerTabs, secondOwnerTabs) {
    const templateSigners = [];

    for (let isi = 0; isi < signers.length; ++isi) {
      const s = signers[isi];

      if (s.roleName === 'Insured' && insuredTabs) {
        templateSigners.push(
          this.shallowMerge(s, {
            tabs: insuredTabs,
          })
        );
      } else if (s.roleName === 'Owner' && ownerTabs) {
        templateSigners.push(
          this.shallowMerge(s, {
            tabs: ownerTabs,
          })
        );
      } else if (s.roleName === 'SecondOwner' && secondOwnerTabs) {
        templateSigners.push(
          this.shallowMerge(s, {
            tabs: secondOwnerTabs,
          })
        );
      } else {
        templateSigners.push(s);
      }
    }

    if (composite.inlineTemplates.length) {
      LoggerSingleton.getInstance().info(
        {
          module: 'DocuSign',
          function: 'assignTabsToComposite',
          category: Category.request,
        },
        'Assigning ' + templateSigners.length + ' recipient signers to DocuSign composite'
      );
      for (let i = 0; i < composite.inlineTemplates.length; i++) {
        const inlineTemplate = composite.inlineTemplates[i];
        inlineTemplate.recipients.signers = templateSigners;
      }
    }
  }

  setProperties(entity, template, historyIn, isArray) {
    let propKey = entity || 'applicant';
    const prefix = entity ? entity + '.' : '';
    const entityProps = {};

    // Avoid circular references
    if (historyIn[template.__name__]) {
      return;
    }

    historyIn[template.__name__] = true;

    // If template has subclasses include them as well
    const properties = template.getProperties();

    getSCProps(template);

    function getSCProps(template) {
      if (template.__children__) {
        template.__children__.forEach(function (childTemplate) {
          _.map(childTemplate.getProperties(), function (prop, key) {
            properties[key] = prop;
          });

          getSCProps(childTemplate);
        });
      }
    }

    this.properties[propKey] = this.properties[propKey] || [];

    for (let propName in properties) {
      const prop = properties[propName];

      if (
        prop.applicationOmit === false ||
        (!prop.isObsolete &&
          !prop.applicationOmit &&
          !prop.isLocal && //prop.toClient != false &&
          !propName.match(/primaryCustomer|alternateCustomer|applicant|policies|capitalNeeds|profile|quoteEngine/))
      ) {
        if (prop.type && prop.type.getProperties) {
          // Make a unique copy of entity history for each property
          const typeHistory = {};

          for (const typeTemplateName in historyIn) {
            typeHistory[typeTemplateName] = true;
          }
          propName = propName.replace('_docusign_', '');
          this.setProperties(prefix + propName, prop.type, typeHistory, false);
        }

        if (prop.of && prop.of.getProperties) {
          // Make a unique copy of entity history for each property
          const ofHistory = {};

          for (const ofTemplateName in historyIn) {
            ofHistory[ofTemplateName] = true;
          }
          propName = propName.replace('_docusign_', '');
          this.setProperties(prefix + propName, prop.of, ofHistory, true);
        }

        if (
          prop.type === String ||
          prop.type === Boolean ||
          prop.type === Date ||
          prop.type === Number ||
          (prop.type && prop.type.getProperties)
        ) {
          this.properties[propKey].push(propName);
          entityProps[propName] = true;
        }
      }
    }

    // If template has subclasses include them as well
    const prototype = {};

    for (const f in template.prototype) {
      if (includeInPrototype(template, f)) {
        prototype[f] = template.prototype[f];
      }
    }

    getSCProto(template);

    function getSCProto(template) {
      if (template.__children__) {
        template.__children__.forEach(function (childTemplate) {
          for (const f in childTemplate.prototype) {
            if (includeInPrototype(childTemplate, f)) {
              prototype[f] = childTemplate.prototype[f];
            }
          }

          getSCProto(childTemplate);
        });
      }
    }

    function includeInPrototype(_template, f) {
      try {
        if (f.match(/^(get|is|has|had|count)([A-Z]+)/i) || f.match(/(.*)Get[A-Z]/)) {
          return true;
        }

        return false;
      } catch (e) {
        LoggerSingleton.getInstance().error(
          {
            module: 'DocuSign',
            function: 'setProperties:includeInPrototype',
            category: Category.request,

            error: {
              name: e?.name,
              message: e?.message,
              stack: e?.stack,
              isHumanRelated: false,
            },
          },
          'error matching property'
        );

        return false;
      }
    }

    for (const funcName in prototype) {
      if (funcName.match(/^(get|is|has|had|count)([A-Z]*)/i) || funcName.match(/(.*)Get[A-Z]/)) {
        if (prototype[funcName] && prototype[funcName].__returns__ && prototype[funcName].__returns__.getProperties) {
          // Make a unique copy of entity history for each property
          const history = {};

          for (const templateName in historyIn) {
            history[templateName] = true;
          }

          this.setProperties(
            prefix + funcName + '()',
            prototype[funcName].__returns__,
            history,
            prototype[funcName].__returnsarray__ ? true : false
          );
        } else {
          this.properties[propKey].push(funcName + '()');
        }
      }
    }

    this.entities.push(propKey);

    if (isArray) {
      // These are to handle the old case when we had properties on a
      // template with the following pattern:
      //
      // funcName: {
      //      of: <returnType>, // (typically returns an array)
      //      body: function() {
      //          ...;
      //      }
      // }
      // Now we use a dummy property with one or both of the _docusign_
      // or the Varying_ portion added to it, and possibly add a getter
      // instead of a function
      propKey = propKey.replace('_docusign_', '');
      propKey = propKey.replace('Varying_', 'Varying()');
      // End new TS shim
      this.arrayEntities.push(propKey);
      this.isArrayEntity[propKey] = true;
    }
  }

  clientInit(customer?) {
    this.injectTemplates(customer, '');
    this.customer = customer;
  }

  injectTemplates(customer, _prefix) {
    this.createProperties();

    // Inject translation for pages and sections into their respective classes
    const docuSign: any = this;

    function PersonValues() {
      const persons = [];
      let blankFound = false;

      for (let ix = 0; ix < customer.persons.length; ++ix) {
        if (!customer.persons[ix].firstName) {
          blankFound = customer.persons[ix];
        }

        persons.push(customer.persons[ix].__id__);
      }

      if (!blankFound) {
        persons.push('blank');
      }

      return persons;
    }

    function PersonDescriptions() {
      const persons = {};
      let blankFound = false;

      for (let ix = 0; ix < customer.persons.length; ++ix) {
        if (customer.persons[ix].firstName) {
          persons[customer.persons[ix].__id__] = customer.persons[ix].getFullName();
        } else {
          blankFound = true;
          persons[customer.persons[ix].__id__] = 'Someone else';
        }
      }

      if (!blankFound) {
        persons['blank'] = 'Someone else';
      }

      return persons;
    }

    function PersonTrigger() {
      if (typeof this.person === 'string') {
        if (this.person === 'blank') {
          const person = Person.new();

          customer.persons.push(person);
          this.person = person;

          return;
        }
      }

      for (let ix = 0; ix < customer.persons.length; ++ix) {
        if (customer.persons[ix].__id__ === this.person) {
          this.person = customer.persons[ix];
        }
      }
    }

    function AddressValues() {
      const addresses = [];
      let blankFound = false;

      for (let ix = 0; ix < customer.addresses.length; ++ix) {
        if (!customer.addresses[ix].street) {
          blankFound = customer.addresses[ix];
        }

        addresses.push(customer.addresses[ix].__id__);
      }

      if (!blankFound) {
        addresses.push('blank');
      }

      return addresses;
    }

    function AddressDescriptions() {
      const addresses = {};
      let blankFound = false;

      for (let ix = 0; ix < customer.addresses.length; ++ix) {
        if (customer.addresses[ix].street) {
          addresses[customer.addresses[ix].__id__] = customer.addresses[ix].getListValue();
        } else {
          blankFound = true;
          addresses[customer.addresses[ix].__id__] = 'Another address';
        }
      }

      if (!blankFound) {
        addresses['blank'] = 'Another address';
      }

      return addresses;
    }

    function AddressTrigger() {
      if (typeof this.address === 'string') {
        if (this.address === 'blank') {
          const address = Address.new();

          customer.addresses.push(address);
          this.address = address;

          return;
        }
      }

      for (let ix = 0; ix < customer.addresses.length; ++ix) {
        if (customer.addresses[ix].__id__ === this.address) {
          this.address = customer.addresses[ix];
        }
      }
    }

    function PhoneValues() {
      const phones = [];
      let blankFound = false;

      for (let ix = 0; ix < customer.phones.length; ++ix) {
        if (!customer.phones[ix].areaCode) {
          blankFound = customer.phones[ix];
        }

        phones.push(customer.phones[ix].__id__);
      }

      if (!blankFound) {
        phones.push('blank');
      }

      return phones;
    }

    function PhoneDescriptions() {
      const phones = {};
      let blankFound = false;

      for (let ix = 0; ix < customer.phones.length; ++ix) {
        if (customer.phones[ix].areaCode) {
          phones[customer.phones[ix].__id__] = customer.phones[ix].getFormated();
        } else {
          blankFound = true;
          phones[customer.phones[ix].__id__] = 'Another phone';
        }
      }

      if (!blankFound) {
        phones['blank'] = 'Another phone';
      }

      return phones;
    }

    function PhoneTrigger() {
      if (typeof this.phone === 'string') {
        if (this.phone === 'blank') {
          const phone = Phone.new();

          customer.phones.push(phone);
          this.phone = phone;

          return;
        }
      }

      for (let ix = 0; ix < customer.phones.length; ++ix) {
        if (customer.phones[ix].__id__ === this.phone) {
          this.phone = customer.phones[ix];
        }
      }
    }

    Beneficiary.inject(function () {
      this.personValues = PersonValues;
      this.personDescriptions = PersonDescriptions;
      this.personTrigger = PersonTrigger;
      this.addressValues = AddressValues;
      this.addressDescriptions = AddressDescriptions;
      this.addressTrigger = AddressTrigger;
      this.phoneValues = PhoneValues;
      this.phoneDescriptions = PhoneDescriptions;
      this.phoneTrigger = PhoneTrigger;
    });

    PackageForm.inject(function () {
      this.formValues = function () {
        const formValues = {};
        const forms = docuSign.getForms();

        for (let ix = 0; ix < forms.length; ++ix) {
          formValues[forms[ix].__id__] = forms[ix].name;
        }

        return forms.length === 0
          ? {
              1: 'Fetching...',
            }
          : formValues;
      };

      this.formAliasValues = function () {
        const formValues = {
          null: 'Not Applicable',
        };

        const forms = docuSign.getForms();

        for (let ix = 0; ix < forms.length; ++ix) {
          formValues[forms[ix].__id__] = forms[ix].name;
        }

        return forms.length === 0
          ? {
              1: 'Fetching...',
            }
          : formValues;
      };

      this.conditionEntityValues = function () {
        const conditionEntities = ['None'];
        const entities = docuSign.entities;

        _.each(
          entities,
          function (entity) {
            let pushed = false;
            const properties = docuSign.properties[entity];

            _.each(
              properties,
              function (property) {
                if (!pushed && property.match(/\(/) && property.match(/^is|has|count/)) {
                  pushed = true;
                  conditionEntities.push(entity);
                }
              }.bind(this)
            );
          }.bind(this)
        );

        return conditionEntities;
      };

      this.conditionFunctionValues = function () {
        const conditions = ['None'];
        const properties = docuSign.properties[this.getConditionEntity(true)];

        _.each(
          properties,
          function (property) {
            if (property.match(/\(/) && property.match(/^is|has|count/)) {
              conditions.push(property);
            }
          }.bind(this)
        );

        return conditions;
      };
    });

    FormField.inject(function () {
      this.sectionValues = function () {
        const sections = [];

        for (let ix = 0; ix < docuSign.sections.length; ++ix) {
          sections.push(docuSign.sections[ix]);
        }

        return sections;
      };

      this.sectionTrigger = function () {
        if (typeof this.section === 'string') {
          for (let ix = 0; ix < docuSign.sections.length; ++ix) {
            if (this.section === docuSign.sections[ix].__id__) {
              this.section = docuSign.sections[ix];
            }
          }
        }
      };

      this.entityValues = function () {
        return docuSign.entities;
      };

      this.propertyValues = function () {
        return docuSign.properties[this.entity];
      };
    });
  }

  @remote({ on: 'server' })
  getForm() {
    //this fn leftover from demo version, left this here just in case UX needs it
    return Form.getFromPersistWithQuery({}).then(
      function (forms) {
        for (let ix = 0; ix < forms.length; ++ix) {
          if (forms[ix].name.match(/_ICC_/)) {
            this.form = forms[ix];
          }
        }

        return true;
      }.bind(this)
    );
  }

  @remote({ on: 'server' })
  getCustomerForms(policy, isAdditionalPackage?: boolean) {
    // Extract owner state and insured state only if different from owner
    const ownerState =
      (policy.trustOrOtherEntity && policy.trustOrOtherEntity.trustSitus) || policy.ownerPersonResidentialAddress.state;

    if (!ownerState) {
      throw new Error('No owner state');
    }

    let insuredState = policy.insured.address.state;

    if (insuredState === ownerState) {
      insuredState = null;
    }

    const product = policy.product || 'CASTerm';
    const carrier = policy.carrier || 'HAVE';
    // Accumulate the forms by reading the package for the owner's state and the insured state
    // including only forms for the insured state that are marked as 'included for insured'
    let combinedForms = [];
    return Promise.resolve()
      .then(gatherPackages.bind(this, ownerState, product))
      .then(gatherForms.bind(this, false, ownerState))
      .then(gatherPackages.bind(this, insuredState, product))
      .then(gatherForms.bind(this, true, insuredState))
      .then(function () {
        return combinedForms;
      });

    async function gatherPackages(state, product) {
      let query: string;
      query = `"state" =  '${state}'
                  AND "carrier" = '${carrier}'
                  AND "product" = '${product}'`;
      query = isAdditionalPackage
        ? query + `AND "isAdditionalPackage" is TRUE`
        : query + `AND "isAdditionalPackage" is NOT TRUE`;
      return state ? Package.getFromPersistWithQuery((knex) => knex.whereRaw(query)) : Promise.resolve([]);
    }

    function gatherForms(onlyForInsured, state, packages) {
      if (packages.length > 0) {
        // Only allow forms for insured state when package is in production or we are running in test
        if (
          onlyForInsured &&
          !packages.inProduction &&
          this.amorphic.config.nconf.get('environment') === 'production'
        ) {
          return Promise.resolve();
        }

        return packages[0].getFormsFor(this.customer, onlyForInsured).then(function (forms) {
          combinedForms = combinedForms.concat(forms);
          return Promise.resolve();
        });
      }

      if (!onlyForInsured) {
        throw new Error('No Package for ' + state + ' / ' + carrier);
      }

      return Promise.resolve();
    }
  }

  getValue(__js__, _noReport) {
    try {
      //tslint:disable-next-line
      return eval(__js__);
    } catch (e) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'DocuSign',
          function: 'getValue',
          category: Category.request,
        },
        `${__js__} : ${e.message}`
      );

      this.error = true;

      return '';
    }
  }

  getIteratorPrefix() {
    return '[this.customer.fieldIterator]';
  }

  buildEmailContent(channelName, language, express?: boolean) {
    const emailSubjectEnglish = 'Sign Your Life Insurance Application';
    const emailSubjectSpanish = 'Firma tu solicitud de seguro de vida.';
    const emailBodySpanish =
      'Tu solicitud de seguro de vida está lista para tu firma electrónica. ' +
      'Para comenzar a firmar, haz clic en el siguiente enlace. Por favor, ' +
      'firma esta solicitud dentro de los siguientes 7 días, de otro modo tu solicitud caducará ' +
      'y se te pedirá que vuelvas a presentar la solicitud.\n \n Atentamente, \n' +
      channelName;

    const timeToEsign = Assumptions.cpSignatureDays;

    let emailBodyEnglish =
      `Your life insurance application is ready for your eSignature. ` +
      `To start signing, click the link below. Please sign this application within the next ${timeToEsign} days, ` +
      `otherwise your application will expire and you will be asked to re-apply.`;

    if (express) {
      emailBodyEnglish +=
        '\n \nAfter you complete your eSignature, your agent is also ' +
        'responsible for signing the application materials. Afterwards, you can then expect to ' +
        'receive your issued policy documents in a timely fashion.';
    }
    emailBodyEnglish += '\n \n Thanks, \n' + channelName + '\n\n';

    const emailTexts = {
      en: {
        body: emailBodyEnglish,
        subject: emailSubjectEnglish,
      },
      es: {
        body: emailBodySpanish,
        subject: emailSubjectSpanish,
      },
    };

    return emailTexts[language];
  }

  async generateApplicationHtml(policy: Policy, config: ServerSideRendererConfig): Promise<string> {
    const applicationId = policy.application._id;
    try {
      return await SnapshotServiceUtils.generateAppFormSnapshotHTML(applicationId, {
        showHeaders: true,
        showApprovedResults: true,
        config: config,
      });
    } catch (e) {
      LoggerSingleton.getInstance().error(
        {
          module: 'DocuSign',
          function: 'generateApplicationHtml',
          category: Category.request,

          error: {
            name: e?.name,
            message: e?.message,
            stack: e?.stack,
            isHumanRelated: false,
          },
        },
        `Failed to generate html snapshot for application id ${policy.application._id}: ${e}`
      );
      throw new Error('Failed to generate html snapshot: ' + e);
    }
  }

  generateApplicationPdf(policy: Policy, content: string): Promise<string> {
    const nodes = this.metadata && this.metadata.children;
    const documentName = CommonUtils.getApplicationFormName(policy, nodes);
    const pdfOptions = {
      format: 'Letter',
      margin: {
        top: '0.50in',
        bottom: '0.75in',
        left: '0.25in',
        right: '0.25in',
      },
      displayHeaderFooter: true,
      headerTemplate: '<span></span>',
      footerTemplate: this.generateApplicationFooterTemplate(policy),
    };

    return this.generatePdf(policy, content, documentName, pdfOptions, POLICY_DOC_SOURCES.app, 'BaseAppPdf', false);
  }

  generateApplicationFooterTemplate(policy: Policy): string {
    const filingNumber = CoverpathUtils.getFilingNumber(policy);
    return `<span style="font-size:12px; margin-left:18pt; font-family:'Arial', sans-serif;">${filingNumber}</span>`;
  }

  generateCirApplicationPdf(policy: Policy, content: string): Promise<string> {
    const nodes = this.metadata && this.metadata.children;
    const documentName = CommonUtils.getApplicationFormName(policy, nodes);
    const pdfOptions = {
      format: 'Letter',
      margin: {
        top: '0.50in',
        bottom: '0.75in',
        left: '0.25in',
        right: '0.25in',
      },
      displayHeaderFooter: true,
      headerTemplate: '<span></span>',
      footerTemplate: this.generateCirApplicationFooterTemplate(),
    };

    return this.generatePdf(policy, content, documentName, pdfOptions, POLICY_DOC_SOURCES.app, 'CirAppPdf', false);
  }

  generateCirApplicationFooterTemplate(): string {
    const filingNumber = CoverpathUtils.getCirFilingNumber();
    return `<span style="font-size:12px; margin-left:18pt; font-family:'Arial', sans-serif;">${filingNumber}</span>`;
  }

  generatePdf(
    policy: Policy,
    content: string,
    documentName: string,
    pdfOptions,
    source: string,
    appType: string,
    savePdf: boolean = true
  ) {
    const launchOptions: object = {};
    const chromeBinPath: string = this.amorphic.config.nconf.get('CHROME_BIN_PATH') || '';
    const disableSandbox: boolean = this.amorphic.config.nconf.get('CHROME_NO_SANDBOX') || false;
    let chromeBrowser;
    let applicationInBase64;
    let applicationPage;

    if (chromeBinPath.length > 0) {
      launchOptions['executablePath'] = chromeBinPath;
    }
    if (disableSandbox) {
      launchOptions['args'] = ['--no-sandbox', '--disable-setuid-sandbox', '--disable-dev-shm-usage'];
    }

    return Promise.resolve()
      .then(() => puppeteer.launch(launchOptions))
      .then((browser) => {
        chromeBrowser = browser;
        return browser.newPage();
      })
      .then((page) => {
        applicationPage = page;
        return applicationPage.setContent(content);
      })
      .then(() => applicationPage.pdf(pdfOptions))
      .then((pdfBuffer) => {
        applicationInBase64 = pdfBuffer.toString('base64');

        if (savePdf) {
          const txn = this.amorphic.begin(true);
          PolicyDoc.new(
            documentName,
            applicationInBase64,
            POLICY_DOC_TYPES.pdf,
            POLICY_DOC_SOURCES.app,
            policy,
            appType,
            null,
            null,
            'en'
          ).setDirty(txn);
          return this.amorphic.end(txn);
        }
      })
      .catch((err) => {
        throw new Error('Unable to insert PDF version of application in composites: ' + err);
      })
      .then(() => {
        if (applicationPage) {
          return applicationPage.close();
        }
      })
      .then(() => {
        if (chromeBrowser) {
          return chromeBrowser.close();
        }
      })
      .then(() => applicationInBase64);
  }

  buildEnvelope(policy: Policy, doSend: boolean, express?: boolean, isAdditionalPackage?: boolean) {
    //Notes about DocuSign setup:
    //1 - Enable Preferences/Account Admin/Features/Display SecureField initial value...
    //2 - Enable Preferences/Account Admin/Features/Attach Certificate of Completion...
    //3 - If using a reviewer create a DocuSign account for them Preferences/User/New with
    //    Sender permission. That EXACT name/email must be used when this function makes envelope requests.
    //    To edit, Reviewers hit Edit Document, then click on Edit Documents and Tags at bottom of screen,
    //    then Correct to send.
    //4 - All templates must have Recipient Role "Insured" w/Order == 2 to which PDF's SecureFields are mapped;
    //    places where Insured must sign should have have Signature tags added.
    //5 - All templates which must be signed by Owner who is NOT the insured must have
    //    an additional Recipient Role of "Owner" w/Order == 2 which only has owner's Signature tabs mapped.
    //6 - In case where signer could be either Owner or Insured, 2 separate templates must be created and
    //    mapped in Haven. One package will have only the Insured Role and the other has both.
    //    In Haven, we must setup Package to conditionally include one package or the other.
    //
    if (!this.properties) {
      this.createProperties();
    }

    //don't know why this is here, remove?
    this.doSend = doSend;
    this.express = express;

    if (!this.controller) {
      this.controller = {
        customer: this.customer,
      };
    } else if (!this.controller.customer) {
      this.controller.customer = this.customer;
    }

    if (!this.pdfController) {
      this.pdfController = PdfController.new({
        customer: this.customer,
        loggedInRole: 'producer',
      });
    }

    return this.getCustomerForms(policy, isAdditionalPackage)
      .then((forms) => this.storeForms(forms))
      .then(() => this.addAllPdfsToComposites(policy, isAdditionalPackage))
      .then((formsAndComposites) => this.generateEnvelope(policy, formsAndComposites, isAdditionalPackage));
  }

  async addAllPdfsToComposites(policy: Policy, isAdditionalPackage?: boolean) {
    const formsAndComposites = {
      forms: this.forms,
      composites: [],
    };
    if (isAdditionalPackage) {
      return formsAndComposites;
    }
    const compositePdfObject = await this.createFormAndComposites(policy);
    formsAndComposites.composites.push(compositePdfObject);

    return formsAndComposites;
  }

  async createFormAndComposites(policy: Policy) {
    if (DocuSign.includeApplicationAsPDF(policy)) {
      const applicationHtml = await this.generateApplicationHtml(policy, {
        showFullSnapshot: false,
        displayBaseForm: true,
      });
      const applicationInBase64 = await this.generateApplicationPdf(policy, applicationHtml);

      let wholeAppBase64 = applicationInBase64;
      if (policy.isCoverpathPolicy() && (policy as CoverpathPolicy).hasCirRider) {
        const cirApplicationHtml = await this.generateApplicationHtml(policy, {
          showFullSnapshot: false,
          displayCirForm: true,
        });
        const cirApplicationInBase64 = await this.generateCirApplicationPdf(policy, cirApplicationHtml);
        wholeAppBase64 = await DocuSign.mergePDFs([applicationInBase64, cirApplicationInBase64]);
      }

      const nodes = this.metadata && this.metadata.children;
      const documentName = CommonUtils.getApplicationFormName(policy, nodes);

      // if we have both cir and base app snapshot, then combine it into one and save it in db as AppPdf
      // otherwise, just save base app as AppPdf
      const txn = this.amorphic.begin(true);
      PolicyDoc.new(
        documentName,
        wholeAppBase64,
        POLICY_DOC_TYPES.pdf,
        POLICY_DOC_SOURCES.app,
        policy,
        'AppPdf',
        null,
        null,
        'en'
      ).setDirty(txn);

      await this.amorphic.end(txn);

      // TODO: Refactor to live with pdf generator and to have a more meaningful ID
      // Note that docusign has a limitation of only using integer ID for document ID.
      // If we find the need to properly identify our documents, this will be an issue
      // (but currently not a problem because we don't store our documents)
      // Was going to use SSN, but realized we shouldn't be sending SSN in requests
      const documentId = APPLICATION_PDF_DOCUMENT_ID;
      const formName = CommonUtils.getApplicationFormName(policy, nodes);
      const compositePdfObject = await this.generateCompositePdf(wholeAppBase64, documentId, '888', formName);

      return compositePdfObject;
    }
  }

  buildVoidEnvelope(policy) {
    const defaultReasons = {
      en: 'Voided by system',
      es: 'Anulado por el sistema',
    };

    const language = policy.submittedLanguage || 'en';
    const reason = defaultReasons[language];

    return Promise.resolve({
      status: 'voided',
      voidedReason: reason,
    });
  }

  @remote({ on: 'server' })
  getEnvelope(envelopeId, envelopeMap, runModes, policy, isAdditionalPackage?: boolean) {
    //Accepts envelope object as returned by createEnvelope
    //Returns an array of {typeName, doc}
    if (!this.properties) {
      this.createProperties();
    }

    //retrieve documents that have form types, plus combined doc that includes certificate
    const map = envelopeMap.docMap;
    const promises = [];

    for (const type in map) {
      const forms = map[type];
      for (let i = 0; i < forms.length; ++i) {
        LoggerSingleton.getInstance().info(
          {
            module: 'DocuSign',
            function: 'getEnvelope',
            category: Category.request,

            data: {
              type,
              forms: forms[i].sequence,
            },
          },
          'Retrieving'
        );
        promises.push(
          this.get(
            'envelopes/' + envelopeId + '/documents/' + forms[i].sequence,
            false,
            runModes,
            type,
            forms[i].language
          )
        );
      }
    }

    LoggerSingleton.getInstance().info(
      {
        module: 'DocuSign',
        function: 'getEnvelope',
        category: Category.request,
      },
      'Retrieving combined packet.'
    );
    promises.push(this.get('envelopes/' + envelopeId + '/documents/combined', false, runModes, 'Packet', 'en'));

    if (!isAdditionalPackage) {
      //param overrides poss acc't setup opt.
      promises.push(
        PolicyDoc.getFromPersistWithQuery({
          policyId: policy._id,
          appType: 'AppPdf',
        })
      );
    }

    return Promise.all(promises)
      .then(
        function (results) {
          const promises = [];
          let result;
          let type;
          let language;
          const resultMap = {};

          //Build resultMap from envelopeDocMap with empty language form arrays
          for (const mapType in map) {
            resultMap[mapType] = {
              en: [],
              es: [],
            };
          }

          //Populate result responses into resultMap language arrays
          for (let i = 0; i < results.length; i++) {
            result = results[i];
            type = result.type;
            language = result.language;

            //Initialize resultMap[type] if not already there
            if (!resultMap[type]) {
              resultMap[type] = {
                en: [],
                es: [],
              };
            }

            if (!resultMap[type][language]) {
              resultMap[type][language] = [];
            }

            if (type === 'App' && DocuSign.includeApplicationAsPDF(policy)) {
              const existingDocs = results[results.length - 1];

              if (existingDocs && existingDocs[0] && !isAdditionalPackage) {
                let doc = existingDocs[0].docContent;
                doc = Buffer.isBuffer(doc) ? doc.toString() : doc;
                const utf8encoded = Buffer.from(doc, 'base64').toString('binary');
                resultMap[type][language].push(utf8encoded);
              }
            }

            resultMap[type][language].push(result.response);
          }

          //Build promises for concatenating pdfs by type & language
          for (const res in resultMap) {
            const enCollect = resultMap[res]['en'];
            const esCollect = resultMap[res]['es'];

            //Check English Pdfs
            if (enCollect.length > 1) {
              LoggerSingleton.getInstance().info(
                {
                  module: 'DocuSign',
                  function: 'getEnvelope',
                  category: Category.request,
                },
                'Retrieved multiple ' + res + ' pdfs in ' + ' English'
              );

              promises.push(pdfConcat.call(this, enCollect, res, 'en'));
            } else if (enCollect.length === 1) {
              LoggerSingleton.getInstance().info(
                {
                  module: 'DocuSign',
                  function: 'getEnvelope',
                  category: Category.request,
                },
                'Retrieved a single ' + res + ' pdf in ' + ' English'
              );

              promises.push(
                Promise.resolve({
                  type: res,
                  language: 'en',
                  pdfString: enCollect[0],
                })
              );
            }

            // Check Spanish Pdfs
            if (esCollect.length > 1) {
              LoggerSingleton.getInstance().info(
                {
                  module: 'DocuS, gn',
                  function: 'getEnvelope',
                  category: Category.request,
                },
                'Retrieved multiple ' + res + ' pdfs in ' + ' Spanish'
              );

              promises.push(pdfConcat.call(this, esCollect, res, 'es'));
            } else if (esCollect.length === 1) {
              LoggerSingleton.getInstance().info(
                {
                  module: 'DocuSign',
                  function: 'getEnvelope',
                  category: Category.request,
                },
                'Retrieved a single ' + res + ' pdf in ' + ' Spanish'
              );

              promises.push(
                Promise.resolve({
                  type: res,
                  language: 'es',
                  pdfString: esCollect[0],
                })
              );
            }
          }

          return Promise.all(promises);
        }.bind(this)
      )
      .then(
        function (results) {
          const retrieved = results.map(function (result) {
            return format(result.type, result.pdfString, result.language);
          });

          return retrieved;

          function format(type, str, language) {
            return {
              typeName: type,
              doc: str,
              language: language,
            };
          }
        }.bind(this)
      );

    function pdfConcat(pdfs, type, language) {
      //result is PDF data in a String
      if (typeof pdfs !== 'object') {
        pdfs = [pdfs];
      }

      const promises = [];

      //create temp files of pdf strings
      let paths = [];

      for (let i = 0; i < pdfs.length; ++i) {
        promises.push(createTmp.call(this, pdfs[i]));
      }

      // on error, files will auto-delete when process terminates
      return Promise.all(promises)
        .then((results) => {
          //results: [name...]
          paths = results;

          //use pdftk to glue the temp files together
          const fileList = '"' + paths.join('" "') + '"';
          const command = 'pdftk ' + fileList + ' cat output -';

          const options = {
            cwd: this.amorphic.config.nconf.get('pdftkPath'),
            encoding: 'binary',
            maxBuffer: 25 * 1024 * 1024,
          };

          return Utils.forkExec(command, options);
        })
        .then(
          function (results) {
            //results: {stdout, stderr}
            const stdout = results.stdout;

            //delete temp files
            for (let i = 0; i < paths.length; ++i) {
              fs.unlink(paths[i], (err) => {
                if (err) {
                  LoggerSingleton.getInstance().error(
                    {
                      module: 'DocuSign',
                      function: 'getEnvelope:pdfConcat',
                      category: Category.request,

                      error: err,
                    },
                    `Error while deleting: ${paths[i]}`
                  );
                }
              });
            }

            //don't wait for completion
            return Promise.resolve({
              type: type,
              language: language,
              pdfString: stdout.toString(),
            });
          },
          function (error) {
            //delete temp files
            for (let i = 0; i < paths.length; ++i) {
              fs.unlink(paths[i], (err) => {
                if (err) {
                  LoggerSingleton.getInstance().error(
                    {
                      module: 'DocuSign',
                      function: 'getEnvelope:pdfConcat',
                      category: Category.request,

                      error: err,
                    },
                    `Error while deleting: ${paths[i]}`
                  );
                }
              });
            }

            //don't wait for completion
            return Promise.reject(error);
          }
        );
    }

    function createTmp(data) {
      //result is Promise.resolve(path)
      let path;
      let fd;

      let tmpFilePath = this.amorphic.config.nconf.get('tmpFilePath');

      if (tmpFilePath.length > 0 && !/[\\/]$/.test(tmpFilePath)) {
        tmpFilePath += '/';
      }

      const opts = {
        dir: tmpFilePath,
      };

      return CommonUtilsBackend.createTempFileAsync(opts)
        .then(function (results) {
          path = results.name;
          fd = results.fileDescriptor;

          const buf = Buffer.from(data, 'binary');

          const opts = {
            flags: 'w',
            fd: fd,
            autoClose: true,
          };

          return CommonUtilsBackend.fsWriteStream(path, opts, buf);
        })
        .catch((error) => {
          LoggerSingleton.getInstance().error(
            {
              module: 'DocuSign',
              function: 'createTmp',
              category: Category.request,

              error: {
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
                isHumanRelated: false,
              },
            },
            'Error while writing stream to temp file'
          );
        });
    }
  }

  @remote({ on: 'server' })
  async get(req, forceParse, runModes?, type?, language?) {
    const options = await this.httpOptions(req, runModes);

    options.encoding = 'binary';

    if (forceParse) {
      options.json = true;
    }

    return request(options)
      .then((response) => {
        if (forceParse) {
          return response;
        }

        return {
          type: type,
          language: language,
          response: response,
        };
      })
      .catch((err) => {
        LoggerSingleton.getInstance().error(
          {
            module: 'DocuSign',
            function: 'get',
            category: Category.request,
            error: {
              name: err?.name,
              message: err?.message,
              stack: err?.stack,
              isHumanRelated: false,
            },
          },
          'DocuSign error'
        );
        throw err;
      });
  }

  resendEnvelope(envelopeId: string) {
    const url = 'envelopes/' + envelopeId + '?resend_envelope=true';
    this.send('PUT', url, {}, 'resend');
  }

  @remote({ on: 'server' })
  async send(httpMethod, req, data, type?: string) {
    // httpMethod is "post" or "put"
    const options = await this.httpOptions(req, null, type);

    options.json = data;
    options.method = httpMethod;

    return request(options).catch(function (err) {
      LoggerSingleton.getInstance().error(
        {
          module: 'DocuSign',
          function: 'send',
          category: Category.request,

          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
          },
        },
        'DocuSign error'
      );
      throw err;
    });
  }

  // Issue with using CoverpathUtilsBackEnd are the circular imports that's happening somewhere
  // Brought the needed function here instead
  static async mergePDFs(files: string[]): Promise<string> {
    const PDFSTREAM_URL = amorphicStatic.config.nconf.get('pdfstreamUrl');
    const headers = {
      'Content-Type': 'application/json',
      'authorization': 'Bearer ' + Utils.getAuthTokenForPdfstream(),
      ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
    };

    const requestBody = {
      files: JSON.stringify(files),
    };

    const options = {
      url: PDFSTREAM_URL + '/merge/Agent',
      json: true,
      body: requestBody,
      method: 'POST',
      headers: headers,
    };

    LoggerSingleton.getInstance().info(
      {
        module: 'DocuSign',
        function: 'mergePDFs',
        category: Category.request,

        data: {
          url: options.url,
        },
      },
      `Merging pdfs`
    );

    return request(options);
  }
}
